import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { CreateGuestModal } from "@/pages/guests/CreateGuestModal";
import { GuestCleanupModal } from "@/pages/guests/GuestCleanupModal";
import { GUEST_TYPES, guestTypeLabel } from "@/lib/guestType";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import type { Guest, GuestLastStay, GuestListResponse, GuestTypeValue } from "@/types/camping";

const SEARCH_DEBOUNCE_MS = 300;

function guestInitials(guest: Guest): string {
  const letters = `${guest.first_name[0] ?? ""}${guest.last_name[0] ?? ""}`.toUpperCase();
  return letters || "?";
}

function contactLine(guest: Guest): string {
  const phone = guest.phone_mobile || guest.phone;
  const place = [guest.postal_code, guest.city].filter(Boolean).join(" ");
  const parts = [place, guest.email, phone].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Keine Kontaktdaten";
}

function stayCountLabel(count: number): string {
  return count === 1 ? "1 Aufenthalt" : `${count} Aufenthalte`;
}

function nightsLabel(nights: number): string {
  return nights === 1 ? "1 Nacht" : `${nights} Nächte`;
}

function LastStaySummary({ stay, stayCount }: { stay: GuestLastStay | null | undefined; stayCount: number }) {
  if (!stay) {
    return (
      <p className="text-sm text-label-tertiary">
        {stay === undefined ? "—" : "Noch kein Aufenthalt"}
      </p>
    );
  }

  const details = [
    `${formatIsoDateShort(stay.arrival_date)} – ${formatIsoDateShort(stay.departure_date)}`,
    stay.pitch_code ? `Platz ${stay.pitch_code}` : null,
    nightsLabel(stay.nights),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="min-w-0">
      <p className="truncate text-sm text-label">{details}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <StatusBadge status={stay.status} />
        <span className="text-xs text-label-tertiary">{stayCountLabel(stayCount)}</span>
      </div>
    </div>
  );
}

function GuestListSkeleton() {
  return (
    <ul className="divide-y divide-separator" aria-hidden>
      {Array.from({ length: 6 }, (_, index) => (
        <li key={index} className="flex animate-pulse items-center gap-4 px-4 py-4 sm:px-5">
          <div className="h-10 w-10 shrink-0 rounded-full bg-fill-tertiary" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-40 max-w-full rounded bg-fill-tertiary" />
            <div className="h-3 w-64 max-w-full rounded bg-fill-tertiary" />
          </div>
          <div className="hidden h-10 w-56 rounded bg-fill-tertiary sm:block" />
        </li>
      ))}
    </ul>
  );
}

export function GuestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canEdit = canWriteGuestPitchBooking(groups);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [totalGuests, setTotalGuests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<GuestTypeValue | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (typeFilter) params.set("guest_type", typeFilter);
    if (searchQuery.length >= 2) params.set("q", searchQuery);
    const query = params.toString() ? `?${params.toString()}` : "";
    api
      .get<GuestListResponse>(`/api/v1/guests${query}`)
      .then((res) => {
        setGuests(res.items);
        setTotalGuests(res.total);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [typeFilter, searchQuery]);

  useEffect(() => {
    setPageMeta({ title: "Gäste", subtitle: "Stammkartei" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    setSearchParams({}, { replace: true });
    navigate(`/guests/${openId}`, { replace: true });
  }, [navigate, searchParams, setSearchParams]);

  const searchHint =
    searchInput.trim().length > 0 && searchInput.trim().length < 2
      ? "Mindestens 2 Zeichen für die Suche"
      : null;

  const resultLabel = loading
    ? "Lade Gäste…"
    : totalGuests === 1
      ? "1 Gast"
      : `${totalGuests} Gäste`;

  return (
    <div className="space-y-4">
      <CreateGuestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />
      <GuestCleanupModal
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        onCompleted={load}
      />

      <section className="rounded-xl border border-separator bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <label className="flex max-w-xl flex-col gap-1 text-sm" data-tour="guests-search">
              <span className="text-label-secondary">Gast suchen</span>
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
                  aria-hidden
                />
                <input
                  type="search"
                  className="eh-input pl-9"
                  placeholder="Name, E-Mail, Telefon, Ort, Ausweis, Notizen…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  autoComplete="off"
                />
              </span>
              {searchHint ? (
                <span className="text-xs text-label-tertiary">{searchHint}</span>
              ) : null}
            </label>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Gästetyp filtern"
              data-tour="guests-type-filter"
            >
              <button
                type="button"
                className={
                  typeFilter === ""
                    ? "rounded-full bg-accent px-3 py-1 text-xs font-medium text-label-on-accent"
                    : "rounded-full border border-separator px-3 py-1 text-xs font-medium text-label-secondary hover:bg-fill-tertiary"
                }
                onClick={() => setTypeFilter("")}
              >
                Alle
              </button>
              {GUEST_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={
                    typeFilter === type.value
                      ? "rounded-full bg-accent px-3 py-1 text-xs font-medium text-label-on-accent"
                      : "rounded-full border border-separator px-3 py-1 text-xs font-medium text-label-secondary hover:bg-fill-tertiary"
                  }
                  onClick={() => setTypeFilter(type.value)}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          {canEdit ? (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => setCleanupOpen(true)}
              >
                Gäste bereinigen
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                data-tour="guests-create"
                onClick={() => setCreateOpen(true)}
              >
                Neuer Gast
              </Button>
            </div>
          ) : null}
        </div>
        <p className="mt-3 text-sm text-label-secondary">
          {resultLabel}
          {!loading && searchQuery.length >= 2 ? ` für „${searchQuery}"` : null}
          {!loading && guests.length < totalGuests ? ` · ${guests.length} angezeigt` : null}
        </p>
      </section>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <section className="overflow-hidden rounded-xl border border-separator bg-card shadow-sm" data-tour="guests-table">
        {loading ? (
          <GuestListSkeleton />
        ) : guests.length === 0 ? (
          <p className="eh-table-empty">
            {searchQuery.length >= 2
              ? "Keine Gäste zu dieser Suche gefunden."
              : "Noch keine Gäste in der Stammkartei."}
          </p>
        ) : (
          <>
            <div className="hidden items-center gap-4 border-b border-separator px-5 py-2 text-xs font-medium uppercase tracking-wide text-label-tertiary sm:flex">
              <span className="w-10 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">Gast</span>
              <span className="w-72 shrink-0 lg:w-80">Letzter Aufenthalt</span>
              <span className="w-4 shrink-0" aria-hidden />
            </div>
            <ul className="divide-y divide-separator">
              {guests.map((guest) => (
                <li key={guest.id}>
                  <button
                    type="button"
                    className={clsx(
                      "flex w-full flex-col gap-3 px-4 py-3.5 text-left transition hover:bg-fill-tertiary focus-visible:bg-fill-tertiary focus-visible:outline-none sm:flex-row sm:items-center sm:gap-4 sm:px-5",
                      guest.is_place_banned && "bg-status-red/6",
                    )}
                    aria-label={`Gast ${guest.last_name}, ${guest.first_name} öffnen`}
                    onClick={() => navigate(`/guests/${guest.id}`)}
                  >
                    <span className="flex min-w-0 flex-1 items-start gap-3">
                      <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
                        {guestInitials(guest)}
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-label">
                            {guest.last_name}, {guest.first_name}
                          </span>
                          <span className="rounded-full bg-fill-secondary px-2 py-0.5 text-[0.7rem] font-medium text-label-secondary">
                            {guestTypeLabel(guest.guest_type)}
                          </span>
                          {guest.is_place_banned ? <PlaceBanBadge /> : null}
                        </span>
                        <span className="mt-0.5 block truncate text-sm text-label-secondary">
                          {contactLine(guest)}
                        </span>
                      </span>
                    </span>
                    <span className="min-w-0 sm:w-72 sm:shrink-0 lg:w-80">
                      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-label-tertiary sm:hidden">
                        Letzter Aufenthalt
                      </span>
                      <LastStaySummary stay={guest.last_stay} stayCount={guest.stay_count ?? 0} />
                    </span>
                    <ChevronRight className="hidden h-4 w-4 shrink-0 text-label-tertiary sm:block" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
